/**
 * storyRepo — direct Supabase reads.
 *
 * Pages must NOT import from `@supabase/supabase-js` directly.
 * All DB access flows through here so we have a single seam for RLS,
 * filtering, and DTO conversion.
 */

import { supabase } from '@/lib/supabase';
import {
  toPublicStoryDto,
  toModerationStoryDto,
  toMyStoryListItemDto,
  toStoryReportDto,
} from '@/lib/dto';

/**
 * Public read: list approved stories (paginated).
 * Optional filters: life_areas[], inner_knots[], obstacles[],
 * mental_states[], inner_qualities[], guna_arc[], story_type.
 *
 * `ascending` flips the created_at ordering. Newest-first is the default
 * because that's what a browsing or searching reader expects; the landing
 * page opts into oldest-first to feature the founding set (see
 * useHomeStories).
 */
export async function listPublicStories({
  limit = 12,
  offset = 0,
  ascending = false,
  life_areas,
  inner_knots,
  obstacles,
  mental_states,
  inner_qualities,
  guna_arc,
  practice_ids,
  professions,
  story_type,
} = {}) {
  let q = supabase
    .from('km_public_stories')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending })
    .range(offset, offset + limit - 1);

  if (story_type) q = q.eq('story_type', story_type);
  if (life_areas?.length)      q = q.overlaps('life_areas',      life_areas);
  if (inner_knots?.length)     q = q.overlaps('inner_knots',     inner_knots);
  if (obstacles?.length)       q = q.overlaps('obstacles',       obstacles);
  if (mental_states?.length)   q = q.overlaps('mental_states',   mental_states);
  if (inner_qualities?.length) q = q.overlaps('inner_qualities', inner_qualities);
  if (guna_arc?.length)        q = q.overlaps('guna_arc',        guna_arc);
  if (practice_ids?.length)    q = q.overlaps('practice_ids',    practice_ids);
  if (professions?.length)     q = q.overlaps('professions',     professions);

  const { data, count, error } = await q;
  if (error) throw error;

  const stories = (data || []).map(toPublicStoryDto);
  return {
    stories,
    total: count ?? stories.length,
    has_more: (offset + stories.length) < (count ?? 0),
  };
}

export async function getPublicStory(id) {
  const { data, error } = await supabase
    .from('km_public_stories')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return toPublicStoryDto(data);
}

/**
 * Author-only: full story shaped like `km_public_stories` for the editor,
 * including pending / needs_revision (not on the public view).
 */
export async function getMyStoryForEdit(storyId) {
  if (!storyId) return null;
  const { data, error } = await supabase.rpc('km_get_my_story_for_edit', {
    p_story_id: storyId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return toPublicStoryDto(row || null);
}

/**
 * Author-only shelf (RPC `km_list_my_stories`). Requires a Supabase session.
 */
export async function listMyStories() {
  const { data, error } = await supabase.rpc('km_list_my_stories');
  if (error) throw error;
  return (data || []).map(toMyStoryListItemDto).filter(Boolean);
}

/**
 * Story type reference data. Returns every type by default — adding a
 * row to km_story_types is enough to surface a new option in the UI.
 *
 * The optional `maxPhase` exists if we ever want to gate rollout again
 * (we used it in v1 to ship only `personal`); pass null/omit to show all.
 */
export async function listStoryTypes({ maxPhase = null } = {}) {
  let q = supabase
    .from('km_story_types')
    .select(`
      id, display_name,
      section_1_label, section_2_label, section_3_label,
      section_4_label, section_5_label, section_count,
      visible_in_phase
    `)
    .order('visible_in_phase')
    .order('display_name');
  if (maxPhase != null) q = q.lte('visible_in_phase', maxPhase);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/**
 * Tag reference data — used to render category-style filters.
 */
export async function listTags(dimension) {
  const { data, error } = await supabase
    .from('km_tags')
    // sanskrit_name + description are needed by LearnPage to show what each
    // tag actually represents; older callers (filter chips) only read
    // display_name + emoji and ignore the extra columns.
    // aliases (text[]) drives the filter-panel typeahead — see
    // SearchFilters.tagHaystack — and the main search-bar typeahead
    // when that lands.
    .select('id, dimension, display_name, sanskrit_name, description, aliases, emoji, sort_order')
    .eq('dimension', dimension)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

/**
 * Moderation reads — RLS allows SELECT * for users in km_moderators.
 */
export async function listPendingStories() {
  const { data, error } = await supabase
    .from('km_stories')
    .select(`
      *,
      km_story_types (
        section_1_label, section_2_label, section_3_label,
        section_4_label, section_5_label, section_count
      )
    `)
    .in('status', ['pending', 'needs_revision'])
    .order('created_at', { ascending: false });
  if (error) throw error;

  // Flatten join shape so DTO converter can read section_N_label as columns.
  return (data || []).map((row) => {
    const labels = row.km_story_types || {};
    return toModerationStoryDto({ ...row, ...labels });
  });
}

/**
 * Moderation: open reader reports (RLS — moderators only).
 */
export async function listOpenStoryReports() {
  const { data, error } = await supabase
    .from('km_story_reports')
    .select(`
      id,
      story_id,
      reporter_id,
      reason,
      details,
      status,
      created_at,
      km_stories (
        id,
        title,
        status,
        author_display_name
      )
    `)
    .eq('status', 'open')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(toStoryReportDto).filter(Boolean);
}

/**
 * Moderation: close a report queue item (`reviewed` = triaged; `dismissed` = not actionable).
 */
export async function resolveStoryReport(reportId, status) {
  if (!reportId || !status) throw new Error('reportId and status required');
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData?.session?.user?.id;
  if (!uid) throw new Error('Not signed in');

  const { error } = await supabase
    .from('km_story_reports')
    .update({
      status,
      reviewed_by: uid,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', reportId);
  if (error) throw error;
}

/**
 * Moderator-only: set story moderation outcome on `km_stories`.
 * Uses Supabase session + RLS (same origin as report queue) — avoids
 * Firebase callable CORS / Cloud Run preflight issues for dashboard actions.
 */
export async function moderateStoryStatus(storyId, status, notes = '') {
  const allowed = new Set(['approved', 'rejected', 'needs_revision']);
  if (!storyId || !allowed.has(status)) {
    throw new Error('storyId and valid status (approved / rejected / needs_revision) required');
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData?.session?.user?.id;
  if (!uid) throw new Error('Not signed in');

  const trimmed = (notes || '').trim();
  const { data, error } = await supabase
    .from('km_stories')
    .update({
      status,
      moderator_notes: trimmed || null,
      reviewed_at: new Date().toISOString(),
      reviewed_by: uid,
    })
    .eq('id', storyId)
    .select('id')
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error('Story not found or you do not have permission to moderate.');
  }
  return { story_id: data.id, status };
}

/**
 * Moderation: close every open report for a story in one shot.
 * Used when a moderator dismisses, reviews, or takes down a whole story.
 */
export async function resolveAllOpenReportsForStory(storyId, status) {
  if (!storyId || !status) throw new Error('storyId and status required');
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData?.session?.user?.id;
  if (!uid) throw new Error('Not signed in');

  const { error } = await supabase
    .from('km_story_reports')
    .update({
      status,
      reviewed_by: uid,
      reviewed_at: new Date().toISOString(),
    })
    .eq('story_id', storyId)
    .eq('status', 'open');
  if (error) throw error;
}

/**
 * Check whether the current user is a moderator.
 * Returns false silently on any error (RLS denies non-moderators).
 */
export async function isCurrentUserModerator(userId) {
  if (!userId) return false;
  const { data, error } = await supabase
    .from('km_moderators')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return false;
  return !!data;
}

/**
 * Whether the signed-in user is the author of this story (for Edit UX).
 * Backed by SECURITY DEFINER RPC `km_i_own_story` — anon / no session → false.
 */
export async function isCurrentUserStoryOwner(storyId) {
  if (!storyId) return false;
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session?.user?.id) return false;

  const { data, error } = await supabase.rpc('km_i_own_story', { p_story_id: storyId });
  if (error) {
    console.warn('[storyRepo] km_i_own_story failed:', error.message);
    return false;
  }
  return !!data;
}
